import {Form, FormProps} from 'platform/components';

import {ReactNode, Suspense, lazy} from 'react';

import {TestIdProps} from 'shared/web';

import {BaseVehicle} from '../core/types/BaseVehicle';
import {AxleCountChips} from './components/AxleCountChips';
import {BatteryCertificateCheckbox} from './components/BatteryCertificateCheckbox';
import {BatteryConditionMeasuredAtDatePicker} from './components/BatteryConditionMeasuredAtDatePicker';
import {BodyStyleChips} from './components/BodyStyleChips';
import {BodyStyleColorSelect} from './components/BodyStyleColorSelect';
import {BodyStyleColorTypeChips} from './components/BodyStyleColorTypeChips';
import {BodyTypeChips} from './components/BodyTypeChips';
import {CarbonDioxideEmissionClassDischargedSelect} from './components/CarbonDioxideEmissionClassDischargedSelect';
import {CarbonDioxideEmissionClassSelect} from './components/CarbonDioxideEmissionClassSelect';
import {CarbonDioxideEmissionClassWeightedCombinedSelect} from './components/CarbonDioxideEmissionClassWeightedCombinedSelect';
import {CarbonDioxideEmissionDischargedField} from './components/CarbonDioxideEmissionDischargedField';
import {CarbonDioxideEmissionField} from './components/CarbonDioxideEmissionField';
import {CarbonDioxideEmissionWeightedCombinedField} from './components/CarbonDioxideEmissionWeightedCombinedField';
import {ConditionChips} from './components/ConditionChips';
import {DimensionLengthSelect} from './components/DimensionLengthSelect';
import {DimensionsHeightOptions} from './components/DimensionsHeightOptions';
import {DoorTypesChips} from './components/DoorTypesChips';
import {DriveChips} from './components/DriveChips';
import {EmissionClassSelect} from './components/EmissionClassSelect';
import {EmissionCo2ClassSelect} from './components/EmissionCo2ClassSelect';
import {FeaturesByManufacturerTextarea} from './components/FeaturesByManufacturerTextarea';
import {FeatureSelector} from './components/FeatureSelector';
import {FirstRegistrationCountrySelect} from './components/FirstRegistrationCountrySelect';
import {HasCocCheckbox} from './components/HasCocCheckbox';
import {InteriorColorSelect} from './components/InteriorColorSelect';
import {InteriorMaterialChips} from './components/InteriorMaterialChips';
import {IsAcceptableForTechnicalInspectionCheckbox} from './components/IsAcceptableForTechnicalInspectionCheckbox';
import {IsRegisteredCheckbox} from './components/IsRegisteredCheckbox';
import {LastRegistrationCountrySelect} from './components/LastRegistrationCountrySelect';
import {LastServiceDatePicker} from './components/LastServiceDatePicker';
import {MakeSelect} from './components/MakeSelect';
import {ModelSelect} from './components/ModelSelect';
import {NextServiceDatePicker} from './components/NextServiceDatePicker';
import {OriginCountrySelect} from './components/OriginCountrySelect';
import {OtherRecordsTextarea} from './components/OtherRecordsTextarea';
import {ParticleFilterCheckbox} from './components/ParticleFilterCheckbox';
import {PrimaryFuelChips} from './components/PrimaryFuelChips';
import {Field} from './components/primitives/Field';
import {Group} from './components/primitives/Group';
import {Section} from './components/primitives/Section';
import {ShowWhen} from './components/primitives/ShowWhen';
import {SubSection} from './components/primitives/SubSection';
import {RealMileageInput} from './components/RealMileageInput';
import {SeatCountChips} from './components/SeatCountChips';
import {ServiceBookStateChips} from './components/ServiceBookStateChips';
import {ServiceBookTypeChips} from './components/ServiceBookTypeChips';
import {ServiceContractNoteTextarea} from './components/ServiceContractNoteTextarea';
import {ServiceContractUntilDatePicker} from './components/ServiceContractUntilDatePicker';
import {SimpleMilageField} from './components/SimpleMilageField';
import {SimpleNumericField} from './components/SimpleNumericField';
import {SimpleTextField} from './components/SimpleTextField';
import {SourceSelect} from './components/SourceSelect';
import {TechnicalInspectionNoteTextarea} from './components/TechnicalInspectionNoteTextarea';
import {TechnicalInspectionValidUntilDatePicker} from './components/TechnicalInspectionValidUntilDatePicker';
import {TitleField} from './components/TitleField';
import {TransmissionChips} from './components/TransmissionChips';
import {VehicleClassSelect} from './components/VehicleClassSelect';
import {VinDecoderField} from './components/VinDecoderField';
import {WarrantyDatePicker} from './components/WarrantyDatePicker';
import {VehicleFormContextValue} from './VehicleFormContext';
import {VehicleFormProvider} from './VehicleFormProvider';

export interface VehicleFormProps<T extends BaseVehicle = BaseVehicle>
  extends Omit<FormProps<T>, 'children'>,
    TestIdProps {
  children: ((context: VehicleFormContextValue<T>) => ReactNode) | ReactNode;
  showMandatoryFields?: boolean;
}

/**
 * Generic vehicle form wrapper that handles XState internally
 * Provides UI primitives for building vehicle forms
 *
 * @example
 * <VehicleForm onSubmit={handleSubmit} schema={vehicleSchema} data-testid="rental-form">
 *   <VehicleForm.Section title="Basic Info">
 *     <VehicleForm.Field name="vin" type="text" />
 *     <VehicleForm.Field name="make" type="choice" options={makeOptions} />
 *   </VehicleForm.Section>
 * </VehicleForm>
 */
export function VehicleForm<T extends BaseVehicle = BaseVehicle>(props: VehicleFormProps<T>) {
  const {children, 'data-testid': dataTestId, showMandatoryFields, ...formProps} = props;

  return (
    <Form<T> {...(formProps as any)} data-testid={dataTestId}>
      {(control, formApi) => (
        <VehicleFormProvider<T>
          control={control}
          formApi={formApi}
          data-testid={dataTestId}
          showMandatoryFields={showMandatoryFields}
        >
          {children}
        </VehicleFormProvider>
      )}
    </Form>
  );
}

// Core form primitives - always loaded (used together)
VehicleForm.Form = VehicleForm; // Alias for the main component
VehicleForm.Section = Section;
VehicleForm.SubSection = SubSection;
VehicleForm.Group = Group;
VehicleForm.Field = Field;
VehicleForm.ShowWhen = ShowWhen;

// Simple fields without extra logic
VehicleForm.SimpleTextField = SimpleTextField;
VehicleForm.SimpleMileageField = SimpleMilageField;
VehicleForm.SimpleNumericField = SimpleNumericField;

// Common components - always loaded / used together
VehicleForm.MakeSelect = MakeSelect;
VehicleForm.ModelSelect = ModelSelect;
VehicleForm.PrimaryFuelChips = PrimaryFuelChips;
VehicleForm.BodyTypeChips = BodyTypeChips;
VehicleForm.BodyStyleChips = BodyStyleChips;
VehicleForm.TitleField = TitleField;
VehicleForm.VinDecoderField = VinDecoderField;
VehicleForm.DoorTypesChips = DoorTypesChips;
VehicleForm.SeatCountChips = SeatCountChips;
VehicleForm.BodyStyleColorTypeChips = BodyStyleColorTypeChips;
VehicleForm.BodyStyleColorSelect = BodyStyleColorSelect;
VehicleForm.InteriorMaterialChips = InteriorMaterialChips;
VehicleForm.InteriorColorSelect = InteriorColorSelect;
VehicleForm.TransmissionChips = TransmissionChips;
VehicleForm.DriveChips = DriveChips;
VehicleForm.ConditionChips = ConditionChips;
VehicleForm.OriginCountrySelect = OriginCountrySelect;
VehicleForm.FirstRegistrationCountrySelect = FirstRegistrationCountrySelect;
VehicleForm.LastRegistrationCountrySelect = LastRegistrationCountrySelect;
VehicleForm.IsRegisteredCheckbox = IsRegisteredCheckbox;
VehicleForm.HasCocCheckbox = HasCocCheckbox;
VehicleForm.RealMileageInput = RealMileageInput;
VehicleForm.OtherRecordsTextarea = OtherRecordsTextarea;
VehicleForm.SourceSelect = SourceSelect;
VehicleForm.ServiceBookTypeChips = ServiceBookTypeChips;
VehicleForm.ServiceBookStateChips = ServiceBookStateChips;
VehicleForm.LastServiceDatePicker = LastServiceDatePicker;
VehicleForm.NextServiceDatePicker = NextServiceDatePicker;
VehicleForm.TechnicalInspectionValidUntilDatePicker = TechnicalInspectionValidUntilDatePicker;
VehicleForm.IsAcceptableForTechnicalInspectionCheckbox = IsAcceptableForTechnicalInspectionCheckbox;
VehicleForm.TechnicalInspectionNoteTextarea = TechnicalInspectionNoteTextarea;
VehicleForm.WarrantyDatePicker = WarrantyDatePicker;
VehicleForm.ServiceContractUntilDatePicker = ServiceContractUntilDatePicker;
VehicleForm.ServiceContractNoteTextarea = ServiceContractNoteTextarea;
VehicleForm.BatteryCertificateCheckbox = BatteryCertificateCheckbox;
VehicleForm.BatteryConditionMeasuredAtDatePicker = BatteryConditionMeasuredAtDatePicker;
VehicleForm.FeatureSelector = FeatureSelector;
VehicleForm.FeaturesByManufacturerTextarea = FeaturesByManufacturerTextarea;
VehicleForm.ParticleFilterCheckbox = ParticleFilterCheckbox;
VehicleForm.EmissionClassSelect = EmissionClassSelect;
VehicleForm.EmissionCo2ClassSelect = EmissionCo2ClassSelect;
VehicleForm.CarbonDioxideEmissionField = CarbonDioxideEmissionField;
VehicleForm.CarbonDioxideEmissionClassSelect = CarbonDioxideEmissionClassSelect;
VehicleForm.CarbonDioxideEmissionDischargedField = CarbonDioxideEmissionDischargedField;
VehicleForm.CarbonDioxideEmissionClassDischargedSelect = CarbonDioxideEmissionClassDischargedSelect;
VehicleForm.CarbonDioxideEmissionWeightedCombinedField = CarbonDioxideEmissionWeightedCombinedField;
VehicleForm.CarbonDioxideEmissionClassWeightedCombinedSelect =
  CarbonDioxideEmissionClassWeightedCombinedSelect;
VehicleForm.AxleCountChips = AxleCountChips;
VehicleForm.DimensionLengthSelect = DimensionLengthSelect;
VehicleForm.DimensionsHeightOptions = DimensionsHeightOptions;
VehicleForm.VehicleClassSelect = VehicleClassSelect;

// Convenience components
VehicleForm.Suspense = (props: {children: ReactNode}) => (
  <Suspense fallback={null}>{props.children}</Suspense>
);

// Specialized components - lazy loaded (conditional usage, has dependencies)
/**
 * Renders fields specific to electric vehicles (e.g., battery capacity).
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.ElectricFields />
 * </VehicleForm.Suspense>
 */
VehicleForm.ElectricFields = lazy(() =>
  import('./components/ElectricFields').then((m) => ({default: m.ElectricFields}))
);
/**
 * Renders chips for selecting a secondary fuel type (e.g., LPG, CNG).
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.SecondaryFuelChips />
 * </VehicleForm.Suspense>
 */
VehicleForm.SecondaryFuelChips = lazy(() =>
  import('./components/SecondaryFuelChips').then((m) => ({default: m.SecondaryFuelChips}))
);
/**
 * Renders chips for selecting a hybrid vehicle type.
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.HybridTypeChips />
 * </VehicleForm.Suspense>
 */
VehicleForm.HybridTypeChips = lazy(() =>
  import('./components/HybridTypeChips').then((m) => ({default: m.HybridTypeChips}))
);

/**
 * Renders a select field for Umweltplakette.
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.UmweltplaketteSelect />
 * </VehicleForm.Suspense>
 */
VehicleForm.UmweltplaketteSelect = lazy(() =>
  import('./components/UmweltplaketteSelect').then((m) => ({default: m.UmweltplaketteSelect}))
);

/**
 * Renders a select field for sale presets.
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.SalePresetSelect />
 * </VehicleForm.Suspense>
 */
VehicleForm.SalePresetSelect = lazy(() =>
  import('./components/SalePresetSelect').then((m) => ({default: m.SalePresetSelect}))
);

// Integration widgets
/*
 * Cebia Autotracer Widget
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.CebiaAutotracerWidget />
 * </VehicleForm.Suspense>
 */
VehicleForm.CebiaAutotracerWidget = lazy(() =>
  import('./components/widgets/CebiaAutotracerWidget').then((m) => ({
    default: m.CebiaAutotracerWidget,
  }))
);

/**
 * Cebia Production Year Widget
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.CebiaProductionYearWidget />
 * </VehicleForm.Suspense>
 */
VehicleForm.CebiaProductionYearWidget = lazy(() =>
  import('./components/widgets/CebiaProductionYearWidget').then((m) => ({
    default: m.CebiaProductionYearWidget,
  }))
);

/**
 * Cebia Integration Widget
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 * Combines autofill widgets for different decoder types.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.CebiaWidget />
 * </VehicleForm.Suspense>
 */
VehicleForm.CebiaAutofillFeaturesWidget = lazy(() =>
  import('./components/widgets/CebiaAutofillFeaturesWidget').then((m) => ({
    default: m.CebiaAutofillFeaturesWidget,
  }))
);

/**
 * Feature Market Analysis Widget
 * This component is lazy-loaded and must be wrapped in a Suspense boundary.
 * Displays market analysis for vehicle features including highlighted and premium features.
 *
 * @example
 * <VehicleForm.Suspense>
 *   <VehicleForm.FeatureMarketAnalysisWidget />
 * </VehicleForm.Suspense>
 */
VehicleForm.FeatureMarketAnalysisWidget = lazy(() =>
  import('./components/widgets/FeatureMarketAnalysisWidget').then((m) => ({
    default: m.FeatureMarketAnalysisWidget,
  }))
);
